import { useEffect, useState } from "react";
import { getWorkflowTypes } from "../../api/v2/TasklistsApi";
import { PhaseWorkflowType } from "../../dtos/PhaseWorkflowType";
import { WorkflowTypeSummaryDto } from "../../dtos/WorkflowTypeSummaryDto";

// Short display labels, kept in enum order. Labels are static so pickers and the
// readonly badge can render immediately; the longer descriptions are loaded from the
// backend so the wording lives in one place (PhaseWorkflowType's EnumMetadata).
export const WORKFLOW_TYPE_LABELS: Record<PhaseWorkflowType, string> = {
    [PhaseWorkflowType.None]: "None",
    [PhaseWorkflowType.Step]: "Step",
    [PhaseWorkflowType.Phase]: "Phase",
    [PhaseWorkflowType.PhaseHybrid]: "Phase Hybrid",
    [PhaseWorkflowType.StepHybrid]: "Step Hybrid"
};

// Order shown in the workflow type picker (matches the backend EnumMetadata order).
export const WORKFLOW_TYPE_ORDER: PhaseWorkflowType[] = [
    PhaseWorkflowType.None,
    PhaseWorkflowType.Phase,
    PhaseWorkflowType.Step,
    PhaseWorkflowType.PhaseHybrid,
    PhaseWorkflowType.StepHybrid
];

// Workflow type metadata is static for the app's lifetime, so fetch it once and share
// the result across every consumer (dialog picker, readonly badge, ...).
let cache: WorkflowTypeSummaryDto[] | null = null;
let inflight: Promise<WorkflowTypeSummaryDto[]> | null = null;

function loadWorkflowTypes(): Promise<WorkflowTypeSummaryDto[]> {
    if (cache) return Promise.resolve(cache);
    if (!inflight) {
        inflight = (async () => {
            try {
                const result = await getWorkflowTypes();
                const data = result?.data;
                if (Array.isArray(data)) {
                    cache = data;
                    return cache;
                }
            } catch {
                // Leave the cache empty so a later mount can retry.
            }
            inflight = null;
            return [];
        })();
    }
    return inflight;
}

// Returns a lookup of workflow type -> description, loaded from the backend and cached.
// Empty until the fetch resolves; callers fall back to label-only display in the meantime.
export function useWorkflowTypeDescriptions(): Map<PhaseWorkflowType, string> {
    const [types, setTypes] = useState<WorkflowTypeSummaryDto[]>(cache ?? []);

    useEffect(() => {
        let active = true;
        loadWorkflowTypes().then((t) => {
            if (active) setTypes(t);
        });
        return () => {
            active = false;
        };
    }, []);

    const map = new Map<PhaseWorkflowType, string>();
    for (const t of types) {
        if (t.description) map.set(t.value, t.description);
    }
    return map;
}
